import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import chatMessagingActions from '../../../pages/Messages/ducks/actions';
import { GET_CHANNELS, GET_CHANNEL_MESSAGES } from '../../../pages/Messages/graphql/queries';
import { SUBSCRIBE_TO_NEW_CHANNEL_MESSAGES } from '../../../pages/Messages/graphql/subscriptions';
import AppSyncClient from '../../../appsync';

// localstorage keys
const CHANNEL_UNREAD_MESSAGE_COUNTS = 'kangarootime-messaging::channel_unread_message_counts';

/**
 * Higher order component that is responsible for listening for new chat messages and updating redux so the badge in the left navbar updates
 */
class ChatMessagesListener extends PureComponent {
  constructor (props) {
    super(props);

    // keep track of subscriptions to all channels
    this.channelSubscriptions = {};
  }

  get unreadChannelCounts () {
    const counts = window.localStorage.getItem(CHANNEL_UNREAD_MESSAGE_COUNTS);

    if (counts) {
      return JSON.parse(counts);
    }

    return null;
  }

  componentDidMount = () => {
    if (
      this.props.channels &&
      this.props.channels.getChannels &&
      this.props.channels.getChannels.length
    ) {
      this.createSubscriptionsToChannels(this.props.channels.getChannels);
      this.setUnreadMessageCounts(this.props.channels.getChannels);
    }
  }

  componentDidUpdate = async (prevProps, prevState) => {
    // previously undefined but now have an array of channels
    if (!prevProps.channels.getChannels && this.props.channels.getChannels && this.props.channels.getChannels.length) {
      this.createSubscriptionsToChannels(this.props.channels.getChannels);
      this.setUnreadMessageCounts(this.props.channels.getChannels);
    }

    if (
      prevProps.channels && this.props.channels &&
      prevProps.channels.getChannels && this.props.channels.getChannels
    ) {
      // channel count has updated
      if (prevProps.channels.getChannels.length !== this.props.channels.getChannels.length) {
        this.createSubscriptionsToChannels(this.props.channels.getChannels);
      }
    }

    // everytime a channel is selected, update localstorage
    if (prevProps.selectedChannelId !== this.props.selectedChannelId) {
      this.writeUnreadMessageCountsToLocalStorage();
    }

    // logged in and authenticated, try refetching our channels
    if (!prevProps.authenticatedUser && this.props.authenticatedUser) {
      if (this.props.channels) {
        this.props.channels.refetch();
      }
    }
  }

  componentWillUnmount = () => {
    this.removeChannelSubscriptions();
  }

  // iterate over array and create subscriptions
  createSubscriptionsToChannels = channels => {
    channels.forEach(channel => {
      if (this.channelSubscriptions[channel.id] === undefined) {
        this.channelSubscriptions[channel.id] = this.subscribeToChannel(channel.id);
      }
    });
  }

  // create the subscription to the channel
  subscribeToChannel = channelId => this.props.channels.subscribeToMore({
    document: SUBSCRIBE_TO_NEW_CHANNEL_MESSAGES,
    variables: { channelId },
    updateQuery: (prev, { subscriptionData }) => {
      const { data: { messagePostedToChannel } } = subscriptionData;

      if (messagePostedToChannel.channelId !== this.props.selectedChannelId) {
        // update redux
        this.props.channelReceivedNewMessage(messagePostedToChannel.channelId);
      } else {
        // write to cache
        // write the new message into cache
        let previousCached = AppSyncClient.readQuery({
          query: GET_CHANNEL_MESSAGES,
          variables: {
            channelId: this.props.selectedChannelId,
            datetime: this.props.channelMessagesInitialFetchTimestamps[this.props.selectedChannelId]
          }
        });

        // add the new message to the front of the array so it appears at the bottom of the channel's message list
        previousCached.getChannelMessagesSince = [messagePostedToChannel, ...previousCached.getChannelMessagesSince];

        // write updates back to the cache
        AppSyncClient.writeQuery({
          query: GET_CHANNEL_MESSAGES,
          variables: {
            channelId: this.props.selectedChannelId,
            datetime: this.props.channelMessagesInitialFetchTimestamps[this.props.selectedChannelId]
          },
          data: previousCached
        });
      }
    },
    onError: error => {
      console.log(error);
      console.log('----');
    }
  });

  removeChannelSubscriptions = () => {
    if (Object.keys(this.channelSubscriptions).length > 0) {
      Object.keys(this.channelSubscriptions).forEach(channelId => {
        const unsubscribe = this.channelSubscriptions[channelId];

        unsubscribe();
      });
    }
  }

  /**
   * @param {Object[]} channels
   */
  setUnreadMessageCounts = channels => {
    if (channels.length) {
      let unreadCounts = {};
      channels.forEach(channel => {
        unreadCounts[channel.id] = channel.unreadMessageCount;
      });
      this.props.restoreUnreadMessageCount(unreadCounts);
    }
  }

  /**
   * write unread message count information for the channels to localstorage
   */
  writeUnreadMessageCountsToLocalStorage = () => {
    const stringified = JSON.stringify(this.props.channelsWithUnreadMessages);
    window.localStorage.setItem(CHANNEL_UNREAD_MESSAGE_COUNTS, stringified);
  }

  render () {
    return this.props.children;
  }
}

const mapStateToProps = state => ({
  authenticatedUser: state.authentication.user,
  selectedChannelId: state.messages.selectedChannel.id,
  channelMessagesInitialFetchTimestamps: state.messages.channelMessagesInitialFetchTimestamps,
  channelsWithUnreadMessages: state.messages.channelsWithUnreadMessages
});

const mapDispatchToProps = dispatch => ({
  channelReceivedNewMessage: channelId => dispatch(chatMessagingActions.channelReceivedNewMessage(channelId)),
  restoreUnreadMessageCount: countsByChanenl => dispatch(chatMessagingActions.restoreUnreadMessageCount(countsByChanenl))
});

export default connect(mapStateToProps, mapDispatchToProps)(graphql(GET_CHANNELS, {
  name: 'channels',
  options: {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  }
})(ChatMessagesListener));
